import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import concat from 'lodash/concat';
import toNumber from 'lodash/toNumber';
import map from 'lodash/map';

import { UserClient, UserID } from '../../../../../../users/usersTypes';

import {
  SmartContractShareIteration,
  SmartContractShareShareType,
  SmartContractShareStatus,
  SmartContractShareStatuses
} from '../../../../../smartContractSharesTypes';
import {
  CreateSmartContractShareFormData,
  CreateSmartContractShareFormUser,
  CreateSmartContractShareFormView
} from '../../CreateSmartContractShareForm.types';

import { useReactHookForm } from '../../../../../../common/hooks/useReactHookForm';

export type CreateSmartContractShareFormDefaultUser =
  CreateSmartContractShareFormUser & { client?: UserClient };

export type CreateSmartContractShareFormSubmitUser =
  CreateSmartContractShareFormUser & {
    status?: SmartContractShareStatus;
    inviteToSmartContract?: boolean;
  };

interface SmartContractSharesFormOptions {
  iteration?: SmartContractShareIteration;
  inviteClientsToSc?: boolean;
  inviteWorkersToSc?: boolean;
  status: SmartContractShareStatus;
  defaultUsers?: CreateSmartContractShareFormDefaultUser[];
  onSubmit: (users: CreateSmartContractShareFormSubmitUser[]) => void;
}

type CheckCreatedUser = { success: UserID[]; failed: UserID[] };

const emptyUserIds: CheckCreatedUser = { success: [], failed: [] };

const createSmartContractShareFormDefaultValues: CreateSmartContractShareFormData =
  {
    users: {
      clients: [],
      workers: []
    },
    iteration: '',
    inviteClientsToSc: false,
    inviteWorkersToSc: true,
    withoutEventIds: []
  };

function useSmartContractSharesForm({
  iteration,
  inviteClientsToSc,
  inviteWorkersToSc,
  status,
  defaultUsers,
  onSubmit
}: SmartContractSharesFormOptions) {
  const checkCreatedUser = useRef<CheckCreatedUser>(emptyUserIds);

  const isEmptyDefaultUsers = isEmpty(defaultUsers);
  const defaultValues = useMemo<CreateSmartContractShareFormData>(() => {
    const defaultIteration = iteration ? iteration.toString() : '';

    return isEmptyDefaultUsers
      ? {
          ...createSmartContractShareFormDefaultValues,
          iteration: defaultIteration,
          inviteClientsToSc:
            inviteClientsToSc ??
            createSmartContractShareFormDefaultValues.inviteClientsToSc,
          inviteWorkersToSc:
            inviteWorkersToSc ??
            createSmartContractShareFormDefaultValues.inviteWorkersToSc
        }
      : {
          users: {
            clients: map(
              defaultUsers.filter((user) => user.client),
              (user) => ({
                userId: user.id,
                nanoId: user.nanoId,
                share: user.share || 0,
                shareType: user.shareType,
                generalLedgerId: user.generalLedgerId,
                fullName: user.fullName,
                image: user.image,
                currentTeam: user.currentTeam
              })
            ),
            workers: map(
              defaultUsers.filter((user) => !user.client),
              (user) => ({
                userId: user.id,
                nanoId: user.nanoId,
                share: user.share || 0,
                shareType: user.shareType,
                generalLedgerId: user.generalLedgerId,
                fullName: user.fullName,
                image: user.image,
                currentTeam: user.currentTeam
              })
            )
          },
          inviteClientsToSc:
            inviteClientsToSc ??
            createSmartContractShareFormDefaultValues.inviteClientsToSc,
          inviteWorkersToSc:
            inviteWorkersToSc ??
            createSmartContractShareFormDefaultValues.inviteWorkersToSc,
          iteration: defaultIteration,
          withoutEventIds:
            createSmartContractShareFormDefaultValues.withoutEventIds
        };
  }, [
    defaultUsers,
    inviteClientsToSc,
    inviteWorkersToSc,
    isEmptyDefaultUsers,
    iteration
  ]);

  const [formView, setFormView] = useState(
    CreateSmartContractShareFormView.SELECT_USERS
  );
  const [allUsersInviteOnlyToTask, setAllUsersInviteOnlyToTask] =
    useState(false);

  const {
    handleSubmitReactHookForm,
    control,
    resetForm,
    watch,
    setValue,
    getValues
  } = useReactHookForm<CreateSmartContractShareFormData>({
    defaultValues,
    isModalForm: true
  });

  const formUsers = watch().users;

  const isEmptyUsers = isEmpty(formUsers.clients) && isEmpty(formUsers.workers);

  const handleResetSmartContractSharesForm = useCallback<() => void>(() => {
    checkCreatedUser.current.failed = [];
    checkCreatedUser.current.success = [];
    resetForm(defaultValues);
    setFormView(CreateSmartContractShareFormView.SELECT_USERS);
  }, [defaultValues, resetForm]);

  const goToChangeShareView = useCallback(async () => {
    setFormView(CreateSmartContractShareFormView.CHANGE_SHARE);
  }, []);

  const goToSelectUsersView = useCallback(async () => {
    setFormView(CreateSmartContractShareFormView.SELECT_USERS);
  }, []);

  useEffect(() => {
    const subscription = watch((data) => {
      if (
        formView === CreateSmartContractShareFormView.SELECT_USERS &&
        status === SmartContractShareStatuses.INVITED
      ) {
        const {
          users: { workers, clients },
          inviteClientsToSc,
          inviteWorkersToSc
        } = data;

        const isWorkersInviteOnly = !isEmpty(workers) && !inviteWorkersToSc;
        const isClientsInviteOnly = !isEmpty(clients) && !inviteClientsToSc;

        setAllUsersInviteOnlyToTask(
          (!data.inviteClientsToSc && !data.inviteWorkersToSc) ||
            (isEmpty(workers) && isClientsInviteOnly) ||
            (isWorkersInviteOnly && isEmpty(clients))
        );
      }
    });
    return () => subscription.unsubscribe();
  }, [formView, isEmptyDefaultUsers, status, watch]);

  const handleSubmitSmartContractShares = useCallback(() => {
    if (
      formView === CreateSmartContractShareFormView.CHANGE_SHARE ||
      allUsersInviteOnlyToTask
    ) {
      return handleSubmitReactHookForm({
        onSubmit: (data: CreateSmartContractShareFormData) => {
          if (isEmpty(data.users.clients) && isEmpty(data.users.workers)) {
            return onSubmit([]);
          }

          const workers = map(data.users.workers, (user) => ({
            id: user.userId,
            nanoId: user.nanoId,
            generalLedgerId: user.generalLedgerId,
            fullName: user.fullName,
            image: user.image,
            currentTeam: user.currentTeam,
            shareType: user.shareType as SmartContractShareShareType,
            share: user.share,
            iteration: data.iteration ? toNumber(data.iteration) : null,
            status,
            inviteToSmartContract: data.inviteWorkersToSc
          }));

          const clients = map(data.users.clients, (user) => ({
            id: user.userId,
            nanoId: user.nanoId,
            generalLedgerId: user.generalLedgerId,
            fullName: user.fullName,
            image: user.image,
            currentTeam: user.currentTeam,
            shareType: user.shareType as SmartContractShareShareType,
            client: true,
            share: user.share,
            iteration: data.iteration ? toNumber(data.iteration) : null,
            status,
            inviteToSmartContract: data.inviteClientsToSc
          }));

          return onSubmit(concat(clients, workers));
        },
        dirtyFieldsOnly: false
      })();
    }

    return goToChangeShareView();
  }, [
    allUsersInviteOnlyToTask,
    formView,
    goToChangeShareView,
    handleSubmitReactHookForm,
    onSubmit,
    status
  ]);

  return {
    control,
    handleResetSmartContractSharesForm,
    handleSubmitSmartContractShares,
    setValue,
    formView,
    goToChangeShareView,
    goToSelectUsersView,
    disabledSubmit: isEmptyUsers,
    getValues,
    oneFormView: allUsersInviteOnlyToTask
  };
}

export default useSmartContractSharesForm;
